import {
  BulkActionContainerCol,
  Col,
  DesignTokens,
  Table,
  TableActionBarRow,
  ValueOrFallback,
} from "melco-ui";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BulkActionCount,
  FormattedDate,
  SharedserviceMelcoModelsDesignCollection,
  Search,
  useTableSelection,
  TableCellLink,
  ViewContext,
} from "melco-shared-logic";
import { PublishedState } from "../../state/PublishedState";
import { useDesignCollections } from "../../../hooks/libraries/designCollections/useDesignCollections";
import { DesignCollectionsListThumbnail } from "./DesignCollectionsListThumbnail";
import { BulkActionDeleteDesignCollections } from "./BulkActionDeleteDesignCollections";

const { spacings } = DesignTokens;

const DETAIL_VIEW_PATH = "/libraries/design-collections";

export const designCollectionsListColumns = (
  t: TFunction,
  context: ViewContext = ViewContext.List
) => {
  return [
    {
      title: "",
      key: "thumbnail",
      dataIndex: "thumbnail",
      render: (_: string, item: SharedserviceMelcoModelsDesignCollection) => (
        <DesignCollectionsListThumbnail designCollection={item} />
      ),
      width: spacings.xlarge + 2 * spacings.middle, // 2 * spacings.middle to compensate for table padding
      height: spacings.xlarge + 2 * spacings.middle, // 2 * spacings.middle to compensate for table padding
    },
    {
      title: t("design_collections_list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoModelsDesignCollection) => (
        <ValueOrFallback
          value={item.name}
          render={(value) =>
            context === ViewContext.List ? (
              <TableCellLink to={[DETAIL_VIEW_PATH, item.id].join("/")}>
                {value}
              </TableCellLink>
            ) : (
              <>{value}</>
            )
          }
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("design_collections_list.columns.design_count"),
      key: "design_count",
      dataIndex: "design_count",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoModelsDesignCollection) => (
        <ValueOrFallback
          value={item.design_count}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("design_collections_list.columns.created_date"),
      key: "created_date",
      dataIndex: "created_date",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoModelsDesignCollection) => (
        <FormattedDate date={item.created_date} />
      ),
    },
    {
      title: t("design_collections_list.columns.updated_date"),
      key: "updated_date",
      dataIndex: "updated_date",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoModelsDesignCollection) => (
        <FormattedDate date={item.updated_date} />
      ),
    },
  ];
};

export const DesignCollectionsList: React.FC = () => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
    refetch,
  } = useDesignCollections();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection((row: SharedserviceMelcoModelsDesignCollection) => {
      navigate([DETAIL_VIEW_PATH, row.id].join("/"));
    });

  const columns = [
    ...designCollectionsListColumns(t),
    {
      title: t("design_collections_list.columns.publish_state"),
      key: "publish_state",
      dataIndex: "publish_state",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoModelsDesignCollection) => {
        return <PublishedState publishedState={item.publish_state ?? 1} />;
      },
    },
  ];

  return (
    <>
      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
        <BulkActionContainerCol>
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionDeleteDesignCollections
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            refetch={refetch}
            data={data}
          />
        </BulkActionContainerCol>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        rowSelection={rowSelection}
        onRow={onRow}
        rowClassName={() => "clickable"}
        noDataMessage={t("design_collections_list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
